import {
  DocStatusCellData,
  FaxJCodeStatus,
  FaxRetrievalStatus,
} from "../../../../data-model/faxDocStatus";
import { FilterSection } from "../../../../data-model/filterValues";

/** Document severity determines row priority; filters/counts still consider every J-code. */
const DOCUMENT_PRIORITY = [
  "denial",
  "additional_info_p2p",
  "unable_to_process",
  "approval",
  "no_auth_required",
  "renewal",
  "others",
  "fax_fetched",
];

export const DOC_STATUS_LABELS: Record<string, string> = {
  approval: "Approval",
  no_auth_required: "No auth required",
  denial: "Denial",
  additional_info_p2p: "Additional Info/P2P",
  unable_to_process: "Unable to process",
  renewal: "Renewal",
  others: "Others",
  fax_fetched: "Fax Fetched",
  fax_not_checked_yet: "Fax not checked yet",
  checking_fax_inbox: "Checking fax inbox",
  checked_no_fax_yet: "No updates",
  fax_check_failed: "Fax check failed",
};

/** Map the diagnostic placeholder only here; its document card retains Unclassified. */
const documentType = (value?: string | null) =>
  value === "unclassified" ? "fax_fetched" : (value ?? "");

/** Select the latest matched document on the highest-ranked unresolved J-code. */
export function getDocStatusCellData(
  jcodes: FaxJCodeStatus[] = [],
  retrieval: FaxRetrievalStatus = {},
  now = Date.now(),
): DocStatusCellData {
  const documents = jcodes.filter(
    (jcode) =>
      jcode.source === "matched" &&
      DOCUMENT_PRIORITY.includes(documentType(jcode.document_type)),
  );
  const unresolved = documents.filter((jcode) => jcode.has_unresolved);
  const candidates = unresolved.length ? unresolved : documents;
  const selected = [...candidates].sort((a, b) => {
    const priority =
      DOCUMENT_PRIORITY.indexOf(documentType(a.document_type)) -
      DOCUMENT_PRIORITY.indexOf(documentType(b.document_type));
    if (priority) return priority;
    const fetched =
      (Date.parse(b.fetched_at ?? "") || 0) -
      (Date.parse(a.fetched_at ?? "") || 0);
    return fetched || a.j_code_id.localeCompare(b.j_code_id);
  })[0];

  if (selected) {
    const nextCheck =
      selected.retrieval?.next_check_at ?? retrieval.next_check_at;
    const status = documentType(selected.document_type);
    return {
      status,
      text: DOC_STATUS_LABELS[status],
      timestamps: [
        { label: "Fetched at", value: selected.fetched_at },
        ...(["approval", "no_auth_required"].includes(status)
          ? []
          : [{ label: "Next check at", value: nextCheck }]),
      ],
    };
  }

  const checkedJcodes = jcodes.filter((jcode) => jcode.retrieval);
  if (checkedJcodes.length) {
    retrieval =
      [...checkedJcodes].sort(
        (a, b) =>
          (Date.parse(b.retrieval?.started_at ?? "") || 0) -
          (Date.parse(a.retrieval?.started_at ?? "") || 0),
      )[0].retrieval ?? retrieval;
  }
  const expired =
    retrieval.state === "checking" &&
    !!retrieval.expires_at &&
    Date.parse(retrieval.expires_at) < now;
  const state = expired ? "failed" : retrieval.state;
  const status =
    state === "checking"
      ? "checking_fax_inbox"
      : state === "checked"
        ? "checked_no_fax_yet"
        : state === "failed"
          ? "fax_check_failed"
          : "fax_not_checked_yet";
  const lastLabel =
    state === "checking"
      ? "Started checking at"
      : state === "checked"
        ? "Last checked at"
        : state === "failed"
          ? "Last attempted at"
          : null;
  return {
    status,
    text: DOC_STATUS_LABELS[status],
    timestamps: [
      ...(lastLabel
        ? [
            {
              label: lastLabel,
              value:
                state === "checked"
                  ? retrieval.checked_at
                  : retrieval.started_at,
            },
          ]
        : []),
      { label: "Next check at", value: retrieval.next_check_at },
    ],
  };
}

/** Counts are returned per J-code by the server, including statuses not shown on a row. */
export function createDocStatusFilter(
  counts?: Record<string, number>,
): FilterSection {
  return {
    id: "doc_status",
    label: "Doc Status",
    type: "string",
    options: Object.entries(DOC_STATUS_LABELS).map(([value, label]) => ({
      value,
      label: counts ? `${label} (${counts[value] ?? 0})` : label,
    })),
  };
}
